import { useState } from 'react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import {
    AlertCircle, CheckCircle, Folder, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useRole } from '@/hooks/auth/useRole';
import { useFolders } from '@/hooks/folders';
import { DocumentWithMetadata } from '@/types/knowledge/knowledge-base';
import { DocumentTypeIcon } from '../DocumentTypeIcon';

interface DocumentMetadataProps {
  document: DocumentWithMetadata;
  documentSize: string;
  totalPages?: number | null;
  onDocumentUpdate?: (
    document: DocumentWithMetadata,
    updates: Partial<DocumentWithMetadata>,
  ) => Promise<any>;
  documentParsingStatus?: Record<string, any>;
  isEditing: boolean;
}

export const DocumentMetadata = ({
  document,
  documentSize,
  totalPages,
  onDocumentUpdate,
  documentParsingStatus,
  isEditing,
}: DocumentMetadataProps) => {
  const { isInternalAdmin } = useRole();
  const [isPublic, setIsPublic] = useState(document.is_public || false);
  const [isActive, setIsActive] = useState(document.is_active || false);
  const { folders } = useFolders();

  const handleVisibilityChange = async (checked: boolean) => {
    setIsPublic(checked);
    if (onDocumentUpdate) {
      const saveToast = toast.loading("Updating document visibility...");
      try {
        await onDocumentUpdate(document, { is_public: checked });
        toast.success(`Document is now ${checked ? "public" : "private"}`, { id: saveToast });
      } catch {
        toast.error("Failed to update visibility", { id: saveToast });
        setIsPublic(!checked);
      }
    }
  };

  const handleActiveChange = async (checked: boolean) => {
    setIsActive(checked);
    if (onDocumentUpdate) {
      const saveToast = toast.loading("Updating document status...");
      try {
        await onDocumentUpdate(document, { is_active: checked });
        toast.success(`Document is now ${checked ? "active" : "inactive"}`, { id: saveToast });
      } catch {
        toast.error("Failed to update status", { id: saveToast });
        setIsActive(!checked);
      }
    }
  };

  const UNCATEGORIZED = "__none__";

  const handleFolderChange = async (value: string) => {
    if (onDocumentUpdate) {
      const saveToast = toast.loading("Moving document...");
      try {
        await onDocumentUpdate(document, { folder_id: value === UNCATEGORIZED ? null : value });
        toast.success(value ? "Document moved to folder" : "Document removed from folder", {
          id: saveToast,
        });
      } catch {
        toast.error("Failed to move document", { id: saveToast });
      }
    }
  };

  const getDocumentStatusIcon = () => {
    const docStatus = document.status || document.metadata?.status;
    const iconMap: Record<string, { Icon: typeof Loader2; color: string; label: string }> = {
      pending: { Icon: Loader2, color: "text-yellow-600", label: "Pending" },
      processing: { Icon: Loader2, color: "text-yellow-600", label: "Processing" },
      in_progress: { Icon: Loader2, color: "text-yellow-600", label: "Processing" },
      completed: { Icon: CheckCircle, color: "text-green-600", label: "Completed" },
      failed: { Icon: AlertCircle, color: "text-red-600", label: "Failed" },
    };
    const config = iconMap[docStatus || ""] ?? {
      Icon: AlertCircle,
      color: "text-gray-600",
      label: "Unknown",
    };
    return {
      ...config,
      isAnimated: docStatus === "processing" || docStatus === "in_progress",
    };
  };

  const { Icon: StatusIcon, color: statusColor, label: statusLabel, isAnimated } = getDocumentStatusIcon();
  return (
    <div className="px-6 py-4 border-b bg-slate-50 space-y-4">
      {/* Info strip */}
      <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
        <span className="font-medium text-gray-700">{documentSize}</span>
        {totalPages && (
          <>
            <span className="text-gray-300">•</span>
            <span>{totalPages} pages</span>
          </>
        )}
        {(document.source_type || document.metadata?.source || document.metadata?.type) && (
          <>
            <span className="text-gray-300">•</span>
            <DocumentTypeIcon document={document} iconSize="sm" showTooltip={false} showLabel />
          </>
        )}
        <span className="text-gray-300">•</span>
        <span>Created {new Date(document.created_at || "").toLocaleDateString()}</span>
        {document.owner && (
          <>
            <span className="text-gray-300">•</span>
            <Badge variant="outline" className="text-[10px]">
              <CopyableEmail email={document.owner.full_name || document.owner.email} />
            </Badge>
          </>
        )}
        <span className="text-gray-300">•</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <StatusIcon className={`h-3 w-3 ${statusColor} ${isAnimated ? "animate-spin" : ""}`} />
                <span className={statusColor}>{statusLabel}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent><p>{statusLabel}</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className="text-gray-300">•</span>
        {document.el_doc_id ? (
          <Badge className="bg-genie-primary/10 text-genie-primary border-0 gap-1">
            <CheckCircle className="h-3 w-3" />
            In KB
          </Badge>
        ) : (
          <Badge className="bg-genie-secondary/10 text-genie-secondary border-0 gap-1">
            <AlertCircle className="h-3 w-3" />
            Not in KB
          </Badge>
        )}
        <span className="text-gray-300">•</span>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 shrink-0">Active</span>
            <Switch
              checked={isActive}
              onCheckedChange={handleActiveChange}
              disabled={!isInternalAdmin()}
              className="h-4 w-7 data-[state=checked]:bg-[#4E9CFF] [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3"
            />
          </div>
        ) : (
          <Badge variant={isActive ? "default" : "secondary"} className="text-[10px]">
            {isActive ? "Active" : "Inactive"}
          </Badge>
        )}
        {isInternalAdmin() && (
          <>
            <span className="text-gray-300">•</span>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 shrink-0">Public</span>
                <Switch
                  checked={isPublic}
                  onCheckedChange={handleVisibilityChange}
                  className="data-[state=checked]:bg-[#4E9CFF]"
                />
              </div>
            ) : (
              <Badge variant={isPublic ? "default" : "secondary"} className="text-[10px]">
                {isPublic ? "Public" : "Private"}
              </Badge>
            )}
          </>
        )}
        <span className="text-gray-300">•</span>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 shrink-0">Folder</span>
            <Select value={document.folder_id || UNCATEGORIZED} onValueChange={handleFolderChange}>
              <SelectTrigger className="h-7 text-xs w-40">
                <SelectValue placeholder="Uncategorized" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNCATEGORIZED}>Uncategorized</SelectItem>
                {folders?.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1">
                  <Folder className="h-3 w-3" />
                  <span>
                    {folders?.find((f) => f.id === document.folder_id)?.name ?? "Uncategorized"}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{folders?.find((f) => f.id === document.folder_id)?.name ?? "Uncategorized"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};
